import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/providers/authContext';
import { Button } from '@/components/ui';

import type { InactivityStatus } from './contracts';
import { useRequestReactivationMutation } from './queries';
import styles from './InactivityLockedScreen.module.css';

export interface InactivityLockedScreenProps {
  readonly status: InactivityStatus;
}

/**
 * "Acesso suspenso por inatividade" (Figma I7), in place of the whole shell.
 *
 * Every unit of the customer is blocked and the policy locks the login with them. The person is
 * signed in — Lucca's decision of 2026-10-16: sign in, restricted mode — but every tenant route
 * answers 403 `customer_locked_inactivity`, so there is nothing to navigate to. What is left is what the
 * screen offers: asking for the units back, and signing out.
 */
export function InactivityLockedScreen({ status }: InactivityLockedScreenProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const request = useRequestReactivationMutation();
  const [sent, setSent] = useState(false);

  const threshold = status.blocked.find((unit) => unit.threshold_days != null)?.threshold_days;
  const alreadyAsked =
    status.blocked.length > 0 && status.blocked.every((unit) => unit.reactivation_requested);
  const asked = sent || alreadyAsked;

  const askForAll = async () => {
    const pending = status.blocked.filter((unit) => !unit.reactivation_requested);
    try {
      for (const unit of pending) {
        await request.mutateAsync({ establishmentId: unit.establishment_id });
      }
      setSent(true);
    } catch {
      // The button stays, with the message below; asking again is safe — one open request per
      // unit, and the API answers the open one.
    }
  };

  const leave = async () => {
    try {
      await signOut();
    } finally {
      queryClient.clear();
      await navigate('/login', { replace: true });
    }
  };

  return (
    <div className={styles.layout}>
      <aside className={styles.story} aria-hidden="true">
        <span className={styles.storyBrand}>
          <span className={styles.storyMark}>In</span>
          Instoque
        </span>
        <p className={styles.storyTitle}>O pedido da semana, em poucos cliques.</p>
        <p className={styles.storyFoot}>Plataforma de compra B2B para food service</p>
      </aside>

      <main className={styles.main}>
        <div className={styles.column}>
          <h1 className={styles.title}>Acesso suspenso</h1>

          <div className={styles.alert} role="alert">
            <p className={styles.alertTitle}>
              <span aria-hidden="true">⚠ </span>Acesso suspenso por inatividade
            </p>
            <p className={styles.alertText}>
              {`Todas as unidades de ${status.customer_name} estão bloqueadas porque não houve pedidos nos últimos ${String(threshold ?? 90)} dias. Peça a reativação para voltar a comprar.`}
            </p>
          </div>

          {user?.email != null ? (
            <p className={styles.signedIn}>
              Você entrou como <strong>{user.email}</strong>
            </p>
          ) : null}

          {status.blocked.length > 0 ? (
            <ul className={styles.units} aria-label="Unidades bloqueadas">
              {status.blocked.map((unit) => (
                <li key={unit.establishment_id}>
                  <span>{unit.name}</span>
                  <span className={styles.unitState}>
                    {unit.reactivation_requested || sent ? 'Reativação pedida' : 'Bloqueada'}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          {asked ? (
            <p className={styles.sent} role="status">
              Pedido de reativação enviado. A equipe da Instoque libera normalmente no mesmo dia
              útil.
            </p>
          ) : (
            <Button
              size="lg"
              fullWidth
              loading={request.isPending}
              loadingLabel="Enviando…"
              onClick={() => {
                void askForAll();
              }}
            >
              Pedir reativação
            </Button>
          )}
          {request.isError ? (
            <p className={styles.error} role="alert">
              Não foi possível enviar o pedido. Tente de novo em instantes.
            </p>
          ) : null}

          <button
            type="button"
            className={styles.leave}
            onClick={() => {
              void leave();
            }}
          >
            ‹ Sair e voltar para o login
          </button>
        </div>
      </main>
    </div>
  );
}
